import { useEffect, useState, useCallback, useRef } from "react";
import {
  X,
  Trash2,
  Plus,
  Save,
  CheckCircle2,
  Circle,
  MessageCircle,
  History,
  CornerDownRight,
  Archive,
  Bell,
  Paperclip,
  Download,
  Upload,
  FileText,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import RichTextEditor from "@/components/ui/rich-text";
import {
  STATUS_LABELS,
  STATUS_ORDER,
  PRIORITY_LABELS,
  formatDueDate,
  initials,
  statusOrder,
} from "@/lib/taskHelpers";

export default function TaskDetailDrawer({ taskId, onClose, labels, statuses, members, onUpdated, onDeleted }) {
  const { currentWorkspace } = useWorkspace();
  const [task, setTask] = useState(null);
  const [subtasks, setSubtasks] = useState([]);
  const [comments, setComments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [newSubtask, setNewSubtask] = useState("");
  const [newComment, setNewComment] = useState("");
  const [newReminderAt, setNewReminderAt] = useState("");
  const [newReminderMethod, setNewReminderMethod] = useState("in_app");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  // Local description state — drives the RichTextEditor and only
  // patches the backend when the user actually changes something.
  const [descDraft, setDescDraft] = useState("");
  const [descDirty, setDescDirty] = useState(false);
  const open = Boolean(taskId);

  const load = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const [t, s, c, a, r, atts] = await Promise.all([
        api.get(`/tasks/${taskId}`),
        api.get(`/tasks/${taskId}/subtasks`),
        api.get(`/tasks/${taskId}/comments`),
        api.get(`/tasks/${taskId}/activity`),
        api.get(`/tasks/${taskId}/reminders`),
        api.get(`/tasks/${taskId}/attachments`),
      ]);
      setTask(t.data);
      setSubtasks(s.data);
      setComments(c.data);
      setActivity(a.data);
      setReminders(r.data);
      setAttachments(atts.data || []);
      setDescDraft(t.data?.description || "");
      setDescDirty(false);
    } catch (e) {
      toast.error("Could not load task");
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const patch = async (changes) => {
    try {
      const { data } = await api.patch(`/tasks/${taskId}`, changes);
      setTask(data);
      onUpdated && onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
  };

  const addSub = async () => {
    if (!newSubtask.trim()) return;
    try {
      await api.post(`/tasks/${taskId}/subtasks`, { title: newSubtask.trim() });
      setNewSubtask("");
      load();
    } catch { toast.error("Could not add subtask"); }
  };
  const toggleSub = async (s) => {
    await api.patch(`/tasks/${s.task_id}`, { done: s.status !== "done" });
    load();
  };
  const deleteSub = async (s) => {
    await api.delete(`/tasks/${s.task_id}`);
    load();
  };
  const addComment = async () => {
    if (!newComment.trim()) return;
    try {
      await api.post(`/tasks/${taskId}/comments`, { body: newComment.trim() });
      setNewComment("");
      load();
    } catch { toast.error("Could not add comment"); }
  };
  const deleteComment = async (c) => {
    await api.delete(`/tasks/${taskId}/comments/${c.comment_id}`);
    load();
  };

  const addReminder = async () => {
    if (!newReminderAt) {
      toast.error("Pick a reminder time first");
      return;
    }
    try {
      await api.post(`/tasks/${taskId}/reminders`, {
        remind_at: new Date(newReminderAt).toISOString(),
        method: newReminderMethod,
      });
      setNewReminderAt("");
      load();
      toast.success("Reminder added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add reminder");
    }
  };
  const deleteReminder = async (r) => {
    await api.delete(`/tasks/${taskId}/reminders/${r.reminder_id}`);
    load();
  };

  // ── Attachments ───────────────────────────────────────────────────
  const uploadAttachments = async (files) => {
    if (!files?.length || !taskId) return;
    setUploading(true);
    setUploadProgress(0);
    let ok = 0;
    let fail = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const form = new FormData();
      form.append("file", file);
      try {
        await api.post(`/tasks/${taskId}/attachments`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
          },
        });
        ok += 1;
      } catch (e) {
        fail += 1;
        toast.error(
          `${file.name}: ${e.response?.data?.detail || "Upload failed"}`
        );
      }
    }
    setUploading(false);
    setUploadProgress(0);
    if (ok) toast.success(`${ok} file${ok === 1 ? "" : "s"} uploaded`);
    if (ok > 0) load();
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onFilePick = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) uploadAttachments(files);
  };

  const downloadAttachment = async (att) => {
    try {
      const r = await api.get(
        `/tasks/${taskId}/attachments/${att.attachment_id}/download`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: att.content_type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  const deleteAttachment = async (att) => {
    if (!window.confirm(`Delete "${att.filename}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/tasks/${taskId}/attachments/${att.attachment_id}`);
      toast.success("Attachment removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove");
    }
  };

  const saveDescription = async () => {
    if (!descDirty || !task) return;
    const next = (descDraft || "").trim();
    const cleaned = next === "<p></p>" ? "" : next;
    if (cleaned === (task.description || "")) {
      setDescDirty(false);
      return;
    }
    try {
      await patch({ description: cleaned });
      setDescDirty(false);
    } catch {
      /* patch() already toasted */
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this task permanently? This will also delete its subtasks and comments.")) return;
    try {
      await api.delete(`/tasks/${taskId}`);
      toast.success("Task deleted");
      onDeleted && onDeleted();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="task-detail-drawer">
        {loading || !task ? (
          <p className="text-sm text-[#82828c] mt-10">Loading task…</p>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle className="pr-10">
                <input
                  defaultValue={task.title}
                  onBlur={(e) => { if (e.target.value !== task.title) patch({ title: e.target.value }); }}
                  className="w-full bg-transparent border-none outline-none text-2xl font-semibold"
                  data-testid="detail-title-input"
                />
              </SheetTitle>
            </SheetHeader>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <FieldSmall label="Status">
                <Select value={task.status} onValueChange={(v) => patch({ status: v })}>
                  <SelectTrigger data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(Array.isArray(statuses) && statuses.length
                      ? [...statuses].sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99))
                      : STATUS_ORDER.map((k) => ({ key: k, name: STATUS_LABELS[k] }))
                    ).map((s) => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldSmall>
              <FieldSmall label="Priority">
                <Select value={task.priority} onValueChange={(v) => patch({ priority: v })}>
                  <SelectTrigger data-testid="detail-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldSmall>
              <FieldSmall label="Assignee">
                <Select value={task.assignee_id || "none"} onValueChange={(v) => patch({ assignee_id: v === "none" ? null : v })}>
                  <SelectTrigger data-testid="detail-assignee-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldSmall>
              <FieldSmall label="Due date">
                <input
                  type="date"
                  defaultValue={task.due_date ? task.due_date.slice(0, 10) : ""}
                  onChange={(e) => patch({ due_date: e.target.value ? new Date(e.target.value).toISOString() : null })}
                  className="bloq-input"
                  data-testid="detail-due-input"
                />
              </FieldSmall>
            </div>

            <div className="mt-5">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Description</p>
                {descDirty && (
                  <button
                    type="button"
                    onClick={saveDescription}
                    className="text-xs text-[#005e7a] hover:underline inline-flex items-center gap-1"
                    data-testid="detail-description-save"
                  >
                    <Save className="w-3 h-3" /> Save
                  </button>
                )}
              </div>
              <RichTextEditor
                value={descDraft}
                onChange={(html) => { setDescDraft(html); setDescDirty(true); }}
                placeholder="Add a description…"
                testId="detail-description-input"
              />
            </div>

            <div className="mt-5">
              <p className="text-xs uppercase tracking-widest text-[#82828c] font-medium mb-1.5">Labels</p>
              <div className="flex flex-wrap gap-1.5">
                {labels.map((l) => {
                  const on = (task.label_ids || []).includes(l.label_id);
                  return (
                    <button
                      key={l.label_id}
                      onClick={() => {
                        const next = on
                          ? (task.label_ids || []).filter((x) => x !== l.label_id)
                          : [...(task.label_ids || []), l.label_id];
                        patch({ label_ids: next });
                      }}
                      className="px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors"
                      style={{ background: on ? l.color : `${l.color}1a`, color: on ? "#fff" : l.color, borderColor: l.color }}
                      data-testid={`detail-label-${l.label_id}`}
                    >
                      {l.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <Tabs defaultValue="subtasks" className="mt-7">
              <TabsList>
                <TabsTrigger value="subtasks" data-testid="detail-tab-subtasks">
                  <CornerDownRight className="w-3.5 h-3.5 mr-1.5" /> Subtasks ({subtasks.length})
                </TabsTrigger>
                <TabsTrigger value="comments" data-testid="detail-tab-comments">
                  <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> Comments ({comments.length})
                </TabsTrigger>
                <TabsTrigger value="attachments" data-testid="detail-tab-attachments">
                  <Paperclip className="w-3.5 h-3.5 mr-1.5" /> Files ({attachments.length})
                </TabsTrigger>
                <TabsTrigger value="reminders" data-testid="detail-tab-reminders">
                  <Bell className="w-3.5 h-3.5 mr-1.5" /> Reminders ({reminders.length})
                </TabsTrigger>
                <TabsTrigger value="activity" data-testid="detail-tab-activity">
                  <History className="w-3.5 h-3.5 mr-1.5" /> Activity
                </TabsTrigger>
              </TabsList>

              <TabsContent value="subtasks" className="mt-3">
                <ul className="flex flex-col gap-1">
                  {subtasks.map((s) => (
                    <li key={s.task_id} className="flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-[#f5efe6] group">
                      <button onClick={() => toggleSub(s)} data-testid={`subtask-toggle-${s.task_id}`}>
                        {s.status === "done"
                          ? <CheckCircle2 className="w-4 h-4 text-[#16a34a]" />
                          : <Circle className="w-4 h-4 text-[#82828c]" />}
                      </button>
                      <span className={`text-sm flex-1 ${s.status === "done" ? "line-through text-[#82828c]" : ""}`}>{s.title}</span>
                      <button onClick={() => deleteSub(s)} className="text-[#82828c] hover:text-red-600 opacity-0 group-hover:opacity-100" data-testid={`subtask-delete-${s.task_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2 mt-2">
                  <input
                    value={newSubtask}
                    onChange={(e) => setNewSubtask(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addSub()}
                    placeholder="Add subtask…"
                    className="bloq-input h-9"
                    data-testid="subtask-input"
                  />
                  <button onClick={addSub} className="bloq-btn-primary px-3" data-testid="subtask-add"><Plus className="w-4 h-4" /></button>
                </div>
              </TabsContent>

              <TabsContent value="comments" className="mt-3">
                <ul className="flex flex-col gap-3">
                  {comments.map((c) => (
                    <li key={c.comment_id} className="bloq-card-soft p-3" data-testid={`comment-${c.comment_id}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <Avatar className="w-6 h-6 rounded-md" title={c.user_name || ""}>
                            <AvatarImage src={c.user_avatar || ""} className="rounded-md" />
                            <AvatarFallback className="bg-[#005e7a] text-white text-[10px] rounded-md">{initials(c.user_name)}</AvatarFallback>
                          </Avatar>
                          <p className="text-xs font-medium">{c.user_name}</p>
                          <p className="text-[10px] text-[#82828c]">{new Date(c.created_at).toLocaleString()}</p>
                        </div>
                        <button onClick={() => deleteComment(c)} className="text-[#82828c] hover:text-red-600" data-testid={`comment-delete-${c.comment_id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{c.body}</p>
                    </li>
                  ))}
                  {comments.length === 0 && <p className="text-xs text-[#82828c]">No comments yet.</p>}
                </ul>
                <div className="flex gap-2 mt-3">
                  <input
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addComment()}
                    placeholder="Add a comment…"
                    className="bloq-input h-9"
                    data-testid="comment-input"
                  />
                  <button onClick={addComment} className="bloq-btn-primary px-3" data-testid="comment-add"><Plus className="w-4 h-4" /></button>
                </div>
              </TabsContent>

              <TabsContent value="attachments" className="mt-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={onFilePick}
                  data-testid="attachment-file-input"
                />
                <ul className="flex flex-col gap-2">
                  {attachments.map((att) => (
                    <li
                      key={att.attachment_id}
                      className="bloq-card-soft p-3 flex items-center gap-3 group"
                      data-testid={`attachment-${att.attachment_id}`}
                    >
                      <FileText className="w-4 h-4 text-[#005e7a] flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" title={att.filename}>{att.filename}</p>
                        <p className="text-[10px] text-[#82828c]">
                          {formatBytes(att.size)} · uploaded by {att.uploaded_by_name || "someone"} · {new Date(att.created_at).toLocaleString()}
                        </p>
                      </div>
                      <button
                        onClick={() => downloadAttachment(att)}
                        className="text-[#82828c] hover:text-[#005e7a] p-1"
                        title="Download"
                        aria-label={`Download ${att.filename}`}
                        data-testid={`attachment-download-${att.attachment_id}`}
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteAttachment(att)}
                        className="text-[#82828c] hover:text-red-600 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove"
                        aria-label={`Remove ${att.filename}`}
                        data-testid={`attachment-delete-${att.attachment_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                  {attachments.length === 0 && (
                    <p className="text-xs text-[#82828c] py-2">
                      No files yet. Attach proposals, screenshots, PDFs — anything that lives with this task.
                    </p>
                  )}
                </ul>
                <div className="mt-3 flex items-center gap-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="bloq-btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                    data-testid="attachment-upload-button"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Uploading {uploadProgress}%
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" /> Upload files
                      </>
                    )}
                  </button>
                  <p className="text-[11px] text-[#82828c]">
                    Up to 3 GB per file. Workspace storage applies.
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="activity" className="mt-3">
                {activity.length === 0 ? (
                  <p className="text-xs text-[#82828c]">No activity yet.</p>
                ) : (
                  <ul className="flex flex-col gap-2.5 text-sm">
                    {activity.map((a) => (
                      <li key={a.activity_id} className="flex items-start gap-2" data-testid={`activity-${a.activity_id}`}>
                        <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: "#005e7a" }} />
                        <div>
                          <p className="text-[#1a1a1f]">
                            <span className="font-medium">{a.user_name}</span>{" "}
                            <span className="text-[#4a4a52]">{describeAction(a)}</span>
                          </p>
                          <p className="text-[10px] text-[#82828c]">{new Date(a.created_at).toLocaleString()}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </TabsContent>

              <TabsContent value="reminders" className="mt-3">
                <ul className="flex flex-col gap-2">
                  {reminders.map((r) => (
                    <li key={r.reminder_id} className="rounded-lg bg-[#f5efe6] p-3 flex items-center justify-between group" data-testid={`reminder-${r.reminder_id}`}>
                      <div>
                        <p className="text-sm font-medium">
                          {new Date(r.remind_at).toLocaleString()}
                        </p>
                        <p className="text-[11px] text-[#82828c]">
                          {r.method === "email" ? "Email" : "In-app"} ·{" "}
                          <span className="capitalize">{r.status}</span>
                          {r.method === "email" && r.status === "pending" && (
                            <span className="text-[#b45309]"> · email delivery not yet connected</span>
                          )}
                        </p>
                      </div>
                      <button
                        onClick={() => deleteReminder(r)}
                        className="text-[#82828c] hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`reminder-delete-${r.reminder_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                  {reminders.length === 0 && (
                    <p className="text-xs text-[#82828c] py-2">
                      No reminders yet. Set one and we'll drop a notification in your bell.
                    </p>
                  )}
                </ul>
                <div className="flex flex-wrap gap-2 mt-3">
                  <input
                    type="datetime-local"
                    value={newReminderAt}
                    onChange={(e) => setNewReminderAt(e.target.value)}
                    className="bloq-input h-9 flex-1 min-w-[180px]"
                    data-testid="reminder-input"
                  />
                  <Select value={newReminderMethod} onValueChange={setNewReminderMethod}>
                    <SelectTrigger className="h-9 w-32" data-testid="reminder-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_app">In-app</SelectItem>
                      <SelectItem value="email">Email (coming soon)</SelectItem>
                    </SelectContent>
                  </Select>
                  <button onClick={addReminder} className="bloq-btn-primary px-3 h-9" data-testid="reminder-add">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </TabsContent>
            </Tabs>

            <div className="mt-7 flex flex-wrap gap-2 border-t pt-4">
              <button onClick={() => patch({ status: task.status === "done" ? "todo" : "done" })} className="bloq-btn-primary inline-flex items-center gap-2" data-testid="detail-complete-toggle">
                <CheckCircle2 className="w-4 h-4" /> {task.status === "done" ? "Reopen" : "Mark complete"}
              </button>
              <button onClick={() => patch({ is_archived: !task.is_archived })} className="bloq-btn-secondary inline-flex items-center gap-2" data-testid="detail-archive">
                <Archive className="w-4 h-4" /> {task.is_archived ? "Restore" : "Archive"}
              </button>
              <button onClick={handleDelete} className="bloq-btn-secondary text-[#dc2626] inline-flex items-center gap-2" data-testid="detail-delete">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function FieldSmall({ label, children }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-[#82828c] font-medium mb-1">{label}</p>
      {children}
    </div>
  );
}

function describeAction(a) {
  const map = {
    task_created: "created the task",
    status_changed: `changed status to "${a.new_value}"`,
    priority_changed: `changed priority to "${a.new_value}"`,
    assignee_changed: `changed assignee`,
    due_date_changed: `set due date to ${a.new_value ? new Date(a.new_value).toLocaleDateString() : "—"}`,
    title_changed: `renamed the task to "${a.new_value}"`,
    comment_added: `commented: "${a.new_value}"`,
    subtask_added: `added subtask "${a.new_value}"`,
    attachment_added: `attached "${a.new_value}"`,
    attachment_removed: `removed attachment "${a.old_value}"`,
  };
  return map[a.action_type] || a.action_type;
}

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let value = bytes;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}
